import { useState, useEffect } from 'react';
import { GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useMobileDetection } from './useMobileDetection';
import { useNavigate } from 'react-router-dom';

export function useGoogleLogin() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mobileInfo = useMobileDetection();
  const navigate = useNavigate();

  // Limpeza de flags antigas na inicialização
  useEffect(() => {
    const cleanupOldFlags = () => {
      const loginAttempt = localStorage.getItem('loopra_login_attempt');
      const processingRedirect = localStorage.getItem('loopra_processing_redirect');

      // NÃO limpar flags recentes (menos de 1 minuto)
      if (loginAttempt) {
        const timeSince = Date.now() - parseInt(loginAttempt);
        if (timeSince > 60000) {
          console.log('[DEBUG] Limpando tentativa de login antiga (>1min)');
          localStorage.removeItem('loopra_login_attempt');
        } else {
          console.log('[DEBUG] Tentativa de login recente detectada, mantendo flag');
        }
      }

      // Só limpar flag de processamento se for 'done', nunca 'processing'
      if (processingRedirect === 'done') {
        console.log('[DEBUG] Limpando flag de processamento concluído');
        localStorage.removeItem('loopra_processing_redirect');
      }
    };

    cleanupOldFlags();
  }, []);

  // Tratar o resultado do redirect quando a página carregar - NOVA ABORDAGEM
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const handleRedirectResult = async () => {
      try {
        const loginAttempt = localStorage.getItem('loopra_login_attempt');

        // Se não há tentativa de login recente, não fazer nada
        if (!loginAttempt) {
          console.log('[DEBUG] Nenhuma tentativa de login detectada');
          return;
        }

        const attemptTime = parseInt(loginAttempt);
        const timeSinceAttempt = Date.now() - attemptTime;

        // Se a tentativa é muito antiga (> 5 minutos), limpar e sair
        if (timeSinceAttempt > 300000) {
          console.log('[DEBUG] Tentativa de login muito antiga, limpando...');
          localStorage.removeItem('loopra_login_attempt');
          localStorage.removeItem('loopra_processing_redirect');
          return;
        }

        console.log('[DEBUG] Tentativa de login detectada há', timeSinceAttempt, 'ms');

        // Verificar se já está processando
        const isProcessingRedirect = localStorage.getItem('loopra_processing_redirect');
        if (isProcessingRedirect === 'done') {
          console.log('[DEBUG] Redirect já foi processado');
          return;
        }

        if (isProcessingRedirect === 'processing') {
          console.log('[DEBUG] Redirect já está sendo processado');
          return;
        }

        // Marcar como processando
        localStorage.setItem('loopra_processing_redirect', 'processing');

        console.log('[DEBUG] Verificando resultado do redirect...');

        // Tentar obter o resultado do redirect
        const result = await getRedirectResult(auth);

        if (result) {
          console.log('[DEBUG] getRedirectResult retornou usuário:', result.user?.email);

          // Marcar como concluído
          localStorage.setItem('loopra_processing_redirect', 'done');
          localStorage.setItem('loopra_login_success', 'true');

          // Redirecionar imediatamente
          console.log('[DEBUG] Redirecionando para /home via navigate');
          navigate('/home', { replace: true });

          // Limpar flags após navegação
          setTimeout(() => {
            localStorage.removeItem('loopra_login_attempt');
            localStorage.removeItem('loopra_processing_redirect');
          }, 100);

          return;
        }

        console.log('[DEBUG] getRedirectResult retornou null, usando onAuthStateChanged como fallback');

        // Aguardar um momento para o Firebase processar
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Verificar novamente se o usuário foi autenticado
        const currentUser = auth.currentUser;
        console.log('[DEBUG] Verificando auth.currentUser após aguardar:', currentUser?.email || 'null');

        if (currentUser) {
          console.log('[DEBUG] Usuário encontrado no auth.currentUser!');

          // Marcar como concluído
          localStorage.setItem('loopra_processing_redirect', 'done');
          localStorage.setItem('loopra_login_success', 'true');

          // Redirecionar
          console.log('[DEBUG] Redirecionando para /home via navigate');
          navigate('/home', { replace: true });

          // Limpar flags
          setTimeout(() => {
            localStorage.removeItem('loopra_login_attempt');
            localStorage.removeItem('loopra_processing_redirect');
          }, 100);

          return;
        }

        console.log('[DEBUG] Criando listener onAuthStateChanged');

        // Se getRedirectResult retornar null (comum no mobile), usar onAuthStateChanged
        // para detectar quando o login foi bem sucedido
        let authCheckCount = 0;
        unsubscribe = onAuthStateChanged(auth, (user) => {
          authCheckCount++;
          console.log(`[DEBUG] onAuthStateChanged chamado (${authCheckCount}x):`, user?.email || 'null');

          if (user && loginAttempt) {
            const currentAttemptTime = parseInt(localStorage.getItem('loopra_login_attempt') || '0');

            // Verificar se o login aconteceu após a tentativa
            if (currentAttemptTime === attemptTime) {
              console.log('[DEBUG] ✅ Login detectado via onAuthStateChanged:', user.email);

              // Marcar como concluído
              localStorage.setItem('loopra_processing_redirect', 'done');
              localStorage.setItem('loopra_login_success', 'true');

              // Redirecionar
              console.log('[DEBUG] Redirecionando para /home via navigate');
              navigate('/home', { replace: true });

              // Limpar flags
              setTimeout(() => {
                localStorage.removeItem('loopra_login_attempt');
                localStorage.removeItem('loopra_processing_redirect');
              }, 100);

              // Cancelar o listener
              if (unsubscribe) {
                unsubscribe();
                unsubscribe = null;
              }
            } else {
              console.log('[DEBUG] ⚠️ Usuário detectado mas timestamp não corresponde');
            }
          } else if (!user) {
            console.log('[DEBUG] ⚠️ onAuthStateChanged retornou null');
          }
        });

        // Timeout de segurança: após 10 segundos, limpar flags se nada aconteceu
        setTimeout(() => {
          const stillProcessing = localStorage.getItem('loopra_processing_redirect');
          if (stillProcessing === 'processing') {
            console.log('[DEBUG] Timeout: limpando flags após 10s');
            localStorage.removeItem('loopra_processing_redirect');
            localStorage.removeItem('loopra_login_attempt');

            if (unsubscribe) {
              unsubscribe();
              unsubscribe = null;
            }
          }
        }, 10000);

      } catch (err: unknown) {
        console.error('[ERROR] Erro no redirect result:', err);

        // Limpar flags em caso de erro
        localStorage.removeItem('loopra_processing_redirect');
        localStorage.removeItem('loopra_login_attempt');

        const errorCode = (err as any).code;
        const errorMessage = (err as any).message;

        console.log('[DEBUG] Error Code:', errorCode);
        console.log('[DEBUG] Error Message:', errorMessage);

        if (errorCode === 'auth/unauthorized-domain') {
          setError('Domínio não autorizado. Configure loopra.com.br no Firebase Console.');
        } else {
          setError(errorMessage || 'Erro ao processar login com Google');
        }
      }
    };

    handleRedirectResult();

    // Cleanup ao desmontar
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [navigate, mobileInfo.isMobile]);

  const login = async () => {
    setLoading(true);
    setError(null);

    try {
      // Verificar se já há uma tentativa de login em andamento
      const existingAttempt = localStorage.getItem('loopra_login_attempt');
      const isProcessingRedirect = localStorage.getItem('loopra_processing_redirect');

      if (isProcessingRedirect) {
        console.log('[DEBUG] Redirect já em andamento, cancelando nova tentativa');
        setLoading(false);
        return;
      }

      // Se há uma tentativa recente (menos de 10 segundos), não iniciar nova
      if (existingAttempt && (Date.now() - parseInt(existingAttempt)) < 10000) {
        console.log('[DEBUG] Tentativa de login muito recente, aguardando...');
        setLoading(false);
        return;
      }

      const provider = new GoogleAuthProvider();

      // Configurações específicas para o provider
      provider.addScope('profile');
      provider.setCustomParameters({
        'prompt': 'select_account'
      });

      console.log('[DEBUG] Informações do dispositivo:', {
        isMobile: mobileInfo.isMobile,
        isIOS: mobileInfo.isIOS,
        isAndroid: mobileInfo.isAndroid,
        screenSize: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        userAgent: navigator.userAgent
      });

      // NOVA ESTRATÉGIA: Tentar popup primeiro, mesmo no mobile
      // Se falhar, tenta redirect como fallback
      try {
        console.log('[DEBUG] Tentando signInWithPopup (funciona melhor que redirect)');

        const result = await signInWithPopup(auth, provider);
        console.log('[DEBUG] ✅ Login bem-sucedido via popup:', result.user?.email);

        // Limpar tentativas antigas
        localStorage.removeItem('loopra_login_attempt');
        localStorage.removeItem('loopra_processing_redirect');

        // Aguardar um momento para garantir que o estado foi persistido
        await new Promise(resolve => setTimeout(resolve, 500));

        // Verificar se o usuário está realmente autenticado
        if (auth.currentUser) {
          console.log('[DEBUG] Usuário confirmado no auth.currentUser, navegando...');
          navigate('/home', { replace: true });
        } else {
          console.warn('[DEBUG] ⚠️ Popup sucedeu mas auth.currentUser é null');
        }

        setTimeout(() => setLoading(false), 10);
        return;

      } catch (popupError: any) {
        const popupErrorCode = popupError.code;

        console.log('[DEBUG] Popup falhou:', popupErrorCode);

        // Se o erro for de popup bloqueado/fechado e estamos no mobile, usar redirect
        if (
          mobileInfo.isMobile &&
          (popupErrorCode === 'auth/popup-blocked' ||
           popupErrorCode === 'auth/popup-closed-by-user' ||
           popupErrorCode === 'auth/cancelled-popup-request')
        ) {
          console.log('[DEBUG] Tentando fallback: signInWithRedirect para mobile');

          // Limpar flags anteriores
          localStorage.removeItem('loopra_processing_redirect');
          localStorage.removeItem('loopra_login_success');

          // Marcar tentativa de login no localStorage antes do redirect
          localStorage.setItem('loopra_login_attempt', Date.now().toString());

          await signInWithRedirect(auth, provider);
          // O redirect vai sair da página, então não precisamos setar loading como false
          return;
        }

        // Se for outro tipo de erro, propagar
        throw popupError;
      }
    } catch (err: unknown) {
      console.error('[ERROR] Erro no login:', err);

      // Limpar flags em caso de erro
      localStorage.removeItem('loopra_login_attempt');
      localStorage.removeItem('loopra_processing_redirect');

      const errorCode = (err as any).code;
      const errorMessage = (err as any).message;

      // Log específico para problemas de domínio
      if (errorCode === 'auth/unauthorized-domain') {
        console.error('[ERROR] Domínio não autorizado! Adicione loopra.com.br no Firebase Console > Authentication > Settings > Authorized domains');
      }

      console.log('[DEBUG] Error Code:', errorCode);
      console.log('[DEBUG] Error Message:', errorMessage);

      // Tratamento específico para domínio não autorizado
      if (errorCode === 'auth/unauthorized-domain') {
        setError('Domínio não autorizado. Configure loopra.com.br no Firebase Console.');
        setTimeout(() => setLoading(false), 10);
        return;
      }

      // Tratamento específico para erros de popup que já foram tratados no try/catch interno
      if (errorCode === 'auth/popup-closed-by-user' || errorCode === 'auth/cancelled-popup-request') {
        console.log('[DEBUG] Popup fechado pelo usuário');
        setTimeout(() => setLoading(false), 10);
        return;
      }

      setError(errorMessage || 'Erro ao fazer login com o Google');

      setTimeout(() => setLoading(false), 10);
    }
  };

  return { login, loading, error };
}
